import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { renderRoutes, matchRoutes } from "react-router-config";
import { connect } from "react-redux";
import { parse } from "query-string";
import SovosNavigation from "mosaic-react/sovos-navigation/SovosNavigation";
import { PersistGate } from "redux-persist/integration/react";

import SovosProvider from "mosaic-react/sovos-provider/SovosProvider";

import { LoadingIndicator } from "colbi_web_ui/lib/components";
import { GlobalProvider as ColbiUIProvider } from "colbi_web_ui/lib/state/GlobalProvider";
import { mapProperties, hydrateRoute, stringifyFilter } from "colbi_web_ui/lib/utils";

import Header from "colbi_web_ui/lib/components/layout/Header/Header";
import Connect from "colbi_web_ui/lib/components/Connect";

import { GlobalProvider } from "../../state/globalProvider";

import MainTabs from "../App/MainTabs/MainTabs";
import ModalController from "../App/ModalController/ModalController";
import DialogController from "../App/DialogController/DialogController";

import styles from "./App.module.sass";

import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    downloadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    setDataFileStatusAction,
    deleteFilePermanentlyAction,
    deleteInvalidFilePermanentlyAction,
    hideFileAction,
    downloadFileAction,
    reprocessRepositoryAction,
    genericSearchAction,
    getFindingDetails,
    reprocessFileAction,
} from "../../store/actions";
import I18n from "../../I18n";
import theme from "../../style/_theme.json";

import { persistor } from "../../../src/index";

import proxy from "../../routes/proxy";
import {
    filteredQueriesByModules,
    filteredQueriesByModulesForMenu,
    getFirstModuleOfArray,
} from "../../routes/manageRouting";

import { UserRoles } from "../../enums/userRoles";
import configsByTheme from "../../enums/configsByTheme";

import { submitTicketFormData } from "../../store/actions/modalActions";

import RepositoryStatus from "../../components/RepositoryStatus/RepositoryStatus";
import LanguageDialog from "../../components/LanguageDialog/LanguageDialog";

import { REACT_APP_COLBI_THEME } from "../../environmentVariables";

import { getOrganizationName } from "../../utils/camelize";
import { changeDeepObject, mapPropertiesForMenu } from "../../utils";
import {
    footerProps,
    setAccount,
    decodeUserSession,
    buildStaticLinks,
} from "../../utils/utils";
import { cleanPropertiesNestedInMenu, findProjectById } from "../../utils/mapPropertiesForMenu";
import { deepLink, urlRedirectLink } from "../../utils/routingUtils";

const Provider = ({ children, ...props }) => {
    return (
        <GlobalProvider {...props}>
            <ColbiUIProvider {...props}>{children}</ColbiUIProvider>
        </GlobalProvider>
    );
};

const appTheme = REACT_APP_COLBI_THEME ? REACT_APP_COLBI_THEME.toLocaleLowerCase() : "sovos";
const localesByAppTheme = configsByTheme(appTheme)["locales"];
const freshDeskFormTitleByAppTheme = configsByTheme(appTheme)["freshDeskFormTitle"];

const App = (props) => {
    const {
        modules,
        user,
        fetch,
        menu,
        logout,
        pushModal,
        popModal,
        genericSearchAction,
        setLocale,
        match,
        route,
        location,
        history,
        reload,
        uploads,
        pushDialog,
        uploadFiles,
        download,
        submitAuditNote,
        setDataFileStatus,
        deleteFilePermanently,
        deleteInvalidFilePermanently,
        hideFile,
        downloadFile,
        reprocessRepository,
        auditNote,
        getFindingDetails,
        downloadJSON,
        submitTicket,
        reprocessFiles,
    } = props;

    const { params: matchParams } = match || {};
    let { fiscalYear, importId } = matchParams;
    const { projectId, modulePrefix, locale = "en" } = matchParams;
    const { results: fetchResults, status: fetchStatus } = fetch || {};

    const [userLocale, setUserLocale] = useState(locale);

    const userInformation = decodeUserSession(user);

    const i18n = I18n.use(userLocale);

    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

    const [selectedLink, setSelectedLink] = useState(i18n("overview"));

    const { menuOrganizationProject = [] } = mapProperties(
        {
            menuOrganizationProject: "query(0).list",
        },
        fetchResults,
        fetchStatus,
        App.queries(matchParams),
        props,
        matchParams,
        i18n,
        userLocale,
    );

    const { organizationProjects = {} } = mapProperties(
        {
            organizationProjects: "query(1)",
        },
        fetchResults,
        fetchStatus,
        App.queries(matchParams),
        props,
        matchParams,
        i18n,
        userLocale,
    );

    const getTotalExportLines = fetchResults[`${userLocale}_get_limit_export`] || "";

    const project = organizationProjects
        ? (organizationProjects.projects || []).find(({ id }) => id === projectId) || {
              name: "",
          }
        : { name: "" };

    const { fiscalYears: projectFiscalYears } = project || {};

    const fiscalYears = Object.keys(projectFiscalYears || []);

    if (!fiscalYear && project && projectFiscalYears) {
        fiscalYear = fiscalYears.length > 0 ? Math.max(...fiscalYears) : "current";
    }

    const fiscalYearsWithImportIds = projectFiscalYears || {};
    const importIdsFromUrlFiscalYear = fiscalYearsWithImportIds[fiscalYear];

    if (!importId && importIdsFromUrlFiscalYear && importIdsFromUrlFiscalYear.length) {
        importId = Math.max(...importIdsFromUrlFiscalYear.map(({ id }) => id));
    }

    const repositoryStatus = fetchResults[
        `${userLocale}_repository_status_fiscalYear_${fiscalYear}_projectId_${projectId}`
    ] || { status: 1 };

    const metadata = getOrganizationName(organizationProjects, project);

    //############ Build Generic Menu  ############

    const queriesForMenu = filteredQueriesByModulesForMenu(modules);
    const navData = mapPropertiesForMenu(
        queriesForMenu,
        fetchResults,
        userLocale,
        fiscalYear,
        projectId,
        importId,
    );

    const firstModuleFromProject = getFirstModuleOfArray(modules);
    const moduleConfigs = project
        ? (project.modules || []).find(({ name }) => name === firstModuleFromProject)
        : undefined;
    const importIdToUse =
        moduleConfigs && moduleConfigs.navigation_by_import_id ? importId : "full";

    const params = {
        ...(parse(location.search) || {}),
        ...(matchParams || {}),
    };

    const goto = (page, filters, anchor) => {
        const pageUrl = page
            ? (typeof page.join === "function" ? page.join("/") : page).replace(/\/+/g, "/")
            : null;
        const urlFilters = filters
            ? Array.isArray(filters)
                ? stringifyFilter(filters)
                : stringifyFilter([filters])
            : "";
        const localePrefixed = pageUrl
            ? `${userLocale ? `/${userLocale}${!pageUrl.match(/^\//) ? "/" : ""}` : ""}`
            : null;
        const gotoPage = pageUrl
            ? `${localePrefixed}${pageUrl}${
                  urlFilters.length ? `?filter=${urlFilters}&resetFilters=${Date.now()}` : ""
              }`
            : `${location.pathname}?filter=${urlFilters}&resetFilters=${Date.now()}${
                  anchor ? `#${anchor}` : ""
              }`;
        history.push(gotoPage);
    };

    const routeQueries = (route.queries && route.queries(params)) || [];

    const genericOverview =
        fetchResults[
            `${userLocale}_generic_overview_fiscalYear_${fiscalYear}_importId_${importId}_locale_${userLocale}_modulePrefix_${modulePrefix}_projectId_${projectId}`
        ];

    const getLayout = JSON.parse(genericOverview || "[]");

    changeDeepObject(getLayout, ["component", "cards"], {
        SubmitSalesInfo: { projectData: projectFiscalYears },
    });

    let displayComponents = [];

    displayComponents.push(...route.components);

    if (getLayout.length > 0 && route && route.isOverview) {
        displayComponents.push(...getLayout);
    }

    const children = displayComponents.map((c, index) => {
        const Component = c.component;
        const componentParsedProps = mapProperties(
            c.props,
            fetchResults,
            fetchStatus,
            routeQueries,
            props,
            params,
            i18n,
            userLocale,
        );
        const componentProps = {
            ...componentParsedProps,
            modules,
        };

        return (
            <Component
                key={index}
                modifiers={c.modifiers}
                {...componentProps}
                activity={fetch.activity}
            />
        );
    });

    const title = mapProperties(
        { title: route.title },
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        userLocale,
    ).title;

    const branch = matchRoutes([route, ...(route.routes || [])], location.pathname);

    const branchQueries = branch.reduce((allQueries, { route }) => {
        const innerQueries = route.queries ? route.queries(params) : [];
        allQueries = [...innerQueries];
        return allQueries;
    }, []);

    const downloadableQueries = branchQueries?.filter(({ downloadable }) => downloadable);

    const { navigation } = mapProperties(
        { navigation: route.navigation },
        fetchResults,
        fetchStatus,
        App.queries(params),
        {
            ...props,
            ...navData,
        },
        params,
        i18n,
        userLocale,
    );

    const staticLinks = buildStaticLinks(navigation, history, matchParams, i18n);

    useEffect(() => {
        staticLinks.length &&
            location?.pathname.length &&
            deepLink(staticLinks, location.pathname, setSelectedLink);
    }, [staticLinks, location]);

    const goToSection = () => {
        const pop = history.action === "POP";
        setLocale(userLocale);
        if (!pop && !fetch.activity) {
            if (location.hash) {
                const scrollElement = window.document.querySelector(location.hash);
                if (scrollElement) {
                    const headerOffset = 70;

                    const previousRect =
                        scrollElement.previousElementSibling.getBoundingClientRect();
                    let stuckOffset = previousRect.height;
                    const elementPosition = scrollElement.offsetTop;
                    const offsetPosition = elementPosition - headerOffset - stuckOffset - 100;

                    setTimeout(() => {
                        window.scrollTo({
                            top: offsetPosition,
                            behavior: "smooth",
                        });
                    }, 0);
                }
            } else if (location.search.search("page") === -1) {
                setTimeout(() => {
                    window.scrollTo(0, 0);
                }, 0);
            }
        }
    };

    const goToSectionRef = useRef(goToSection);

    useEffect(() => {
        goToSectionRef.current();
    }, []);

    if (!userInformation) {
        return urlRedirectLink(`/${userLocale}/login`, true);
    }

    if (userInformation && userInformation.expired) {
        return urlRedirectLink(`/${userLocale}/reset_password`);
    }

    if (userInformation && userInformation.role === UserRoles.BACKOFFICE) {
        return urlRedirectLink(`/${userLocale}/settings/`);
    }

    if (!projectId) {
        return urlRedirectLink(`${userLocale ? `/${userLocale}` : ""}/`);
    }

    if (!matchParams.fiscalYear && !matchParams.importId && !fiscalYears.length) {
        return urlRedirectLink(`${userLocale ? userLocale : "en"}/${projectId}/repository`);
    }

    if (
        fiscalYear &&
        fiscalYear !== matchParams.fiscalYear &&
        matchParams.importId !== importIdToUse &&
        firstModuleFromProject
    ) {
        const to = hydrateRoute(match.path, {
            ...matchParams,
            fiscalYear,
            importId: importIdToUse,
            firstModuleFromProject,
        });

        if (to !== match.url && !to.match(/\*$/)) {
            return urlRedirectLink(to);
        }
    }

    const organizationSelectedInMenu = (menuOrganizationProject || []).find(({ nestedAccounts }) =>
        (nestedAccounts || []).some(({ id }) => id === projectId),
    );

    const {
        accounts: fullOrganizationsWithProjects,
        selectedAccount: selectedOrganizationWithProjects,
    } = cleanPropertiesNestedInMenu(menuOrganizationProject, organizationSelectedInMenu);

    return (
        <SovosProvider>
            <Provider
                user={userInformation}
                proxy={proxy}
                theme={theme}
                uploads={uploads}
                actions={{
                    pushModal,
                    pushDialog,
                    popModal,
                    uploadFiles,
                    submitTicket,
                    download,
                    submitAuditNote,
                    removeModal,
                    setDataFileStatus,
                    deleteFilePermanently,
                    deleteInvalidFilePermanently,
                    hideFile,
                    downloadFile,
                    reprocessRepository,
                    logout,
                    getFindingDetails,
                    downloadJSON,
                    setLocale: (userLocale) => {
                        history.push(`/${userLocale}${history.location.pathname.substring(3)}`);
                    },
                    search: genericSearchAction,
                    goto,
                    downloadableQueries,
                    reprocessFiles,
                }}
                i18n={i18n}
                locale={userLocale || localesByAppTheme[0].id}
                locales={localesByAppTheme}
                reload={reload}
                params={{
                    ...params,
                    fiscalYear: params.fiscalYear || fiscalYear,
                    importId: params.importId || importId,
                }}
                history={history}
                location={location}
                repositoryStatus={repositoryStatus}
                auditNote={auditNote}
                totalLinesCsv={getTotalExportLines}
            >
                <PersistGate persistor={persistor}>
                    <div className={styles["app"]}>
                        <div className={`${styles["aside"]} `}>
                            <SovosNavigation
                                product="APR SAF-T"
                                accounts={fullOrganizationsWithProjects}
                                selectedAccount={findProjectById(
                                    selectedOrganizationWithProjects,
                                    projectId,
                                )}
                                saveNavCollapsed={true}
                                links={staticLinks}
                                selectedLink={selectedLink}
                                setAccount={(account) => {
                                    account["id"] = account["id"].split("_").pop();
                                    setAccount(account, locale, history, proxy, params);
                                }}
                                FooterProps={footerProps(
                                    history,
                                    userLocale,
                                    userInformation,
                                    setIsLanguageModalOpen,
                                    logout,
                                    i18n,
                                )}
                            />
                            <LanguageDialog
                                isOpen={isLanguageModalOpen}
                                setIsOpen={setIsLanguageModalOpen}
                                i18n={i18n}
                                setUserLocale={setUserLocale}
                                userLocale={userLocale}
                                localesByAppTheme={localesByAppTheme}
                                setLocale={setLocale}
                                history={history}
                            />
                        </div>
                        <div className={styles["main"]}>
                            <Connect to={["search", "downloading"]}>
                                {({ search, downloading }) => (
                                    <div>
                                        <RepositoryStatus modules={modules} />
                                        <Header
                                            className={styles["header"]}
                                            genericSearchAction
                                            reportsAction
                                            settingsAction={userInformation.accessSettings}
                                            project={project}
                                            hasFullNavigation={importIdToUse === "full"}
                                            fiscalYears={fiscalYearsWithImportIds}
                                            importIds={importIdsFromUrlFiscalYear}
                                            downloadableQueries={downloadableQueries}
                                            title={title}
                                            downloading={downloading}
                                            search={search}
                                            navigation={navigation}
                                            organizations={menuOrganizationProject}
                                            navByImportId={route.navByImportId}
                                            showCsvButton={
                                                route && route.showCsvButton
                                                    ? route.showCsvButton
                                                    : false
                                            }
                                            totalLinesCsv={getTotalExportLines}
                                            emailFrom={userInformation.email}
                                            name={userInformation.name}
                                            metadata={metadata}
                                            freshDeskModalTitle={freshDeskFormTitleByAppTheme}
                                        />
                                    </div>
                                )}
                            </Connect>
                            <main
                                className={`${styles["content"] || ""} ${
                                    styles["foreground"] || ""
                                }`}
                            >
                                {fetch.activity && (
                                    <LoadingIndicator
                                        className={`${styles["loading-indicator"]} ${
                                            menu === "expanded" ? "" : styles["small-margin"]
                                        }`}
                                    />
                                )}
                                {children}
                                {route.routes && route.routes.length > 1 && (
                                    <MainTabs
                                        className={styles["tabs"]}
                                        routes={route.routes}
                                        params={matchParams}
                                    />
                                )}
                                {renderRoutes(route.routes)}
                            </main>
                        </div>
                        <ModalController popModal={popModal} />
                        <DialogController i18n={i18n} />
                    </div>
                </PersistGate>
            </Provider>
        </SovosProvider>
    );
};

App.queries = (args, userModulesByProject) => {
    const queries = filteredQueriesByModules(userModulesByProject);
    const deepCopyQueries = JSON.parse(JSON.stringify(queries));
    if (deepCopyQueries) {
        //needed to change arguments to the respective value
        deepCopyQueries.forEach((query) =>
            Object.keys(query.args).forEach((k) =>
                query.args[k] ? query.args[k] : (query.args[k] = args[k] || query.args[k]),
            ),
        );
    }

    const allQueries = [
        {
            resource: "menu_organizations_projects",

            body: "id,name,typeEntity,nestedAccounts{id,name,typeEntity}",
            forceRefresh: true,
        },
        {
            resource: "organization_projects_by_projectId",
            args: {
                projectId: args.projectId,
            },
            body: "id,name,maxFiscalYear,projects{id,name,fiscalYears,status,taxRegistrationNumber,active,projectExpired,metadata,modules{navigation_by_import_id,name,default_route,default_module}},organizationExpired, organizationActive, maxFiscalYear",
            forceRefresh: true,
        },
        {
            resource: "application_version",
            body: "version",
        },
        {
            resource: "get_environment_to_subject",
        },
        {
            resource: "is_to_display_support_icon",
        },
        {
            resource: "get_limit_export",
        },
        {
            resource: "proxy_redirect_environment",
        },
        ...deepCopyQueries,
    ];
    if (userModulesByProject && userModulesByProject.includes("merge_collisions")) {
        allQueries.push({
            resource: "repository_status",
            args: {
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
            },
            body: "repoStatus,fiscalYear,fileType,autoMerge",
        });
    }

    return allQueries;
};

export default connect(
    ({ user, fetch, menu, uploads, auditNote }) => ({
        user,
        fetch,
        menu,
        uploads,
        auditNote,
    }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
        getFindingDetails,
        setDataFileStatus: setDataFileStatusAction,
        deleteFilePermanently: deleteFilePermanentlyAction,
        deleteInvalidFilePermanently: deleteInvalidFilePermanentlyAction,
        hideFile: hideFileAction,
        downloadFile: downloadFileAction,
        reprocessRepository: reprocessRepositoryAction,
        uploadFiles: uploadAction,
        submitTicket: submitTicketFormData,
        download: downloadAction,
        submitAuditNote: auditNoteSubmit,
        genericSearchAction,
        reprocessFiles: reprocessFileAction,
    },
)(withRouter(App));
